import logger from '../lib/logger';
import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder,
  ChevronRight,
  Home,
  Image,
  Film,
  X,
  Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { contentApi } from '../lib/api';
import { toast } from 'sonner';

const ContentBrowser = ({ 
  open, 
  onClose, 
  onSelect, 
  title = "Select Content",
  filterType = null // 'image' or 'video' or null for all
}) => {
  const [content, setContent] = useState({ folders: [], items: [] });
  const [allContent, setAllContent] = useState({ folders: [], items: [] });
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderPath, setFolderPath] = useState([{ id: null, name: 'Content' }]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('browse'); // 'browse' or 'all'

  const loadContent = useCallback(async (folderId = null) => {
    setIsLoading(true);
    try {
      const response = await contentApi.get(folderId);
      let items = response.data.items || [];
      
      // Filter by type if specified
      if (filterType) {
        items = items.filter(item => item.file_type === filterType);
      }
      
      setContent({
        folders: response.data.folders || [],
        items
      });
      setCurrentFolderId(folderId);
    } catch (error) {
      logger.error('Failed to load content:', error);
      toast.error('Failed to load content');
    } finally {
      setIsLoading(false);
    }
  }, [filterType]);

  const loadAllContent = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await contentApi.getAll();
      let items = response.data.items || [];
      
      // Filter by type if specified
      if (filterType) {
        items = items.filter(item => item.file_type === filterType);
      }
      
      setAllContent({
        folders: response.data.folders || [],
        items
      });
    } catch (error) {
      logger.error('Failed to load all content:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filterType]);

  useEffect(() => {
    if (open) {
      loadContent(null);
      loadAllContent();
      setSelectedItem(null);
      setSearchQuery('');
      setViewMode('browse');
      setFolderPath([{ id: null, name: 'Content' }]);
    }
  }, [open, loadContent, loadAllContent]);

  const navigateToFolder = (folderId, folderName) => {
    if (folderId === null) {
      setFolderPath([{ id: null, name: 'Content' }]);
    } else {
      setFolderPath(prev => [...prev, { id: folderId, name: folderName }]);
    }
    loadContent(folderId);
  };

  const navigateToBreadcrumb = (index) => {
    const newPath = folderPath.slice(0, index + 1);
    setFolderPath(newPath);
    loadContent(newPath[newPath.length - 1].id);
  };

  const handleSelect = () => {
    if (selectedItem) {
      onSelect(selectedItem);
      onClose();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileTypeIcon = (item) => {
    if (item.file_type === 'video') {
      return <Film className="w-4 h-4 text-purple-400" />;
    }
    return <Image className="w-4 h-4 text-blue-400" />;
  };

  // Filter items by search query
  const getFilteredItems = () => {
    const items = viewMode === 'browse' ? content.items : allContent.items;
    if (!searchQuery.trim()) return items;
    
    const query = searchQuery.toLowerCase();
    return items.filter(item => 
      item.name.toLowerCase().includes(query) ||
      item.original_filename.toLowerCase().includes(query)
    );
  };

  const filteredItems = getFilteredItems();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-700 max-w-3xl max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {/* View mode tabs and search */}
        <div className="flex items-center gap-3 py-2">
          <Tabs value={viewMode} onValueChange={setViewMode} className="flex-shrink-0">
            <TabsList className="bg-zinc-800">
              <TabsTrigger value="browse" className="text-xs">Browse</TabsTrigger>
              <TabsTrigger value="all" className="text-xs">All Files</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search files..."
              className="pl-8 bg-zinc-800 border-zinc-700 h-8 text-sm"
            />
          </div>
        </div>

        {/* Breadcrumb (only in browse mode) */}
        {viewMode === 'browse' && (
          <div className="flex items-center gap-1 text-xs text-zinc-400 px-1">
            {folderPath.map((folder, index) => (
              <React.Fragment key={folder.id || 'root'}>
                {index > 0 && <ChevronRight className="w-3 h-3 flex-shrink-0" />}
                <button
                  onClick={() => navigateToBreadcrumb(index)}
                  className={`hover:text-white flex-shrink-0 ${
                    index === folderPath.length - 1 ? 'text-white font-medium' : ''
                  }`}
                >
                  {index === 0 ? <Home className="w-3 h-3" /> : folder.name}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Content area — scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="p-2">
            {isLoading ? (
              <div className="text-center text-zinc-500 py-8">Loading...</div>
            ) : filteredItems.length === 0 && (viewMode !== 'browse' || content.folders.length === 0) ? (
              <div className="text-center text-zinc-500 py-8">
                <p>No content found</p>
                <p className="text-xs mt-1">
                  {filterType === 'image' && "Upload images to the Content library first"}
                  {filterType === 'video' && "Upload videos to the Content library first"}
                  {!filterType && "Upload files to the Content library first"}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Folders (only in browse mode) */}
                {viewMode === 'browse' && content.folders.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-zinc-400 mb-2">Folders</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {content.folders.map(folder => (
                        <div
                          key={folder.id}
                          className="bg-zinc-800/50 rounded-lg p-3 hover:bg-zinc-700/50 cursor-pointer border border-zinc-700/50"
                          onClick={() => navigateToFolder(folder.id, folder.name)}
                        >
                          <div className="flex items-center gap-2">
                            <Folder className="w-5 h-5 text-yellow-500" />
                            <span className="text-sm text-white truncate">{folder.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Files */}
                {filteredItems.length > 0 && (
                  <div>
                    <h4 className="text-xs font-medium text-zinc-400 mb-2">
                      {filterType === 'image' ? 'Images' : filterType === 'video' ? 'Videos' : 'Files'}
                      {searchQuery && ` (${filteredItems.length} results)`}
                    </h4>
                    <div className="grid grid-cols-4 gap-2">
                      {filteredItems.map(item => (
                        <div
                          key={item.id}
                          className={`group relative bg-zinc-800/50 rounded-lg overflow-hidden cursor-pointer border-2 transition-colors ${
                            selectedItem?.id === item.id 
                              ? 'border-blue-500' 
                              : 'border-zinc-700/50 hover:border-zinc-600'
                          }`}
                          onClick={() => setSelectedItem(item)}
                          onDoubleClick={() => {
                            setSelectedItem(item);
                            handleSelect();
                          }}
                        >
                          {/* Thumbnail */}
                          <div className="aspect-video bg-zinc-900 flex items-center justify-center relative">
                            {item.file_type === 'image' ? (
                              <img
                                src={contentApi.getUrl(item.id)}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : item.thumbnail_path ? (
                              <img
                                src={contentApi.getThumbnailUrl(item.id)}
                                alt={item.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <Film className="w-8 h-8 text-zinc-600" />
                            )}
                            {item.file_type === 'video' && item.optimization_status === 'processing' && (
                              <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                                <span className="text-[10px] text-fuchsia-300 font-medium animate-pulse">
                                  Optimizing…
                                </span>
                              </div>
                            )}
                            {item.file_type === 'video' && item.optimization_status === 'failed' && (
                              <div className="absolute bottom-0 inset-x-0 bg-amber-900/80 text-[10px] text-amber-200 text-center py-0.5">
                                Using original
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <div className="p-2">
                            <div className="flex items-center gap-1">
                              {getFileTypeIcon(item)}
                              <span className="text-xs text-white truncate flex-1">{item.name}</span>
                            </div>
                            <div className="text-[10px] text-zinc-500 mt-1">
                              {formatFileSize(item.file_size)}
                              {item.width && item.height && ` • ${item.width}×${item.height}`}
                            </div>
                          </div>
                          
                          {/* Selected indicator */}
                          {selectedItem?.id === item.id && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer with selection info and actions */}
        <DialogFooter className="flex items-center justify-between border-t border-zinc-700 pt-4">
          <div className="text-sm text-zinc-400">
            {selectedItem ? (
              <span>Selected: <span className="text-white">{selectedItem.name}</span></span>
            ) : (
              <span>Click to select, double-click to confirm</span>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSelect} 
              disabled={!selectedItem}
            >
              Select
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ContentBrowser;
